import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  organisationApi,
  OrganisationUserManagementStatus,
  OrganisationUserOrderBy,
  OrganisationUsersFilters,
  User,
} from '../../../services';
import {
  Button,
  ButtonType,
  ConfirmationModal,
  getCssVar,
  IconLabel,
  IconLabelHintDirection,
  ModalSlidePanel,
  OrderDirection,
  ORG_DASH_USERS,
  SearchField,
  useAppDispatch,
  useAppSelector,
  useDebounce,
  useIsNarrowViewport,
} from '../../../shared';
import {
  getOrganisationUsers,
  organisationUserBanByUserId,
  organisationUserUnbanByUserId,
} from '../../../slices';
import { AddUsersInChannelModal } from './AddUsersInChannelModal';
import { AddUsersModal } from './AddUsersModal';
import { UpdateUsersRoleModal } from './UpdateUsersRoleModal';
import { UserChannels, UserChannelsModal } from './UserChannelsModal';
import { UsersColumns } from './UsersColumns';
import { UsersFilters } from './UsersFilters';
import { UsersTable } from './UsersTable';

import classNames from 'classnames';
import classes from './Users.module.scss';

export interface GetOrgUsersArgs {
  page: number;
  query?: string;
  orderBy?: OrganisationUserOrderBy;
  orderDirection?: OrderDirection;
  filters?: OrganisationUsersFilters;
}

export const Users: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsers' });

  const dispatch = useAppDispatch();

  const isNarrowViewport = useIsNarrowViewport();

  const [organisationUserRegeneratePassword] =
    organisationApi.endpoints.organisationUserRegeneratePassword.useMutation();

  const [organisationUserRemindInvitation] =
    organisationApi.endpoints.organisationUserRemindInvitation.useMutation();

  const [organisationMemberRemove] =
    organisationApi.endpoints.organisationMemberRemove.useMutation();

  const [organisationUserGenerateReport] =
    organisationApi.endpoints.organisationUserGenerateReport.useMutation();

  const [cookies] = useCookies([ORG_DASH_USERS]);

  const orgDashUsersCookies = useMemo(() => {
    return { ...cookies[ORG_DASH_USERS] };
  }, [cookies]);

  const { requestFilters = {} } = orgDashUsersCookies;

  const appliedFiltersCount = useMemo(() => Object.keys(requestFilters).length, [requestFilters]);

  const [orderByActive, setOrderByActive] = useState<OrganisationUserOrderBy>(
    OrganisationUserOrderBy.AUTHORIZED_TIME
  );

  const [orderDirectionActive, setOrderDirectionActive] = useState<OrderDirection>(
    OrderDirection.DESC
  );

  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const [addUsersOpen, setAddUsersOpen] = useState<boolean>(false);

  const [banUsersOpen, setBanUsersOpen] = useState<boolean>(false);

  const [unbanUsersOpen, setUnbanUsersOpen] = useState<boolean>(false);

  const [removeUsersOpen, setRemoveUsersOpen] = useState<boolean>(false);

  const [remindUsersOpen, setRemindUsersOpen] = useState<boolean>(false);

  const [regeneratePasswordUsersOpen, setRegeneratePasswordUsersOpen] = useState<boolean>(false);

  const [banLoading, setBanLoading] = useState<boolean>(false);

  const [unbanLoading, setUnbanLoading] = useState<boolean>(false);

  const [removeLoading, setRemoveLoading] = useState<boolean>(false);

  const [regeneratePasswordLoading, setRegeneratePasswordLoading] = useState<boolean>(false);

  const [remindUsersLoading, setRemindUsersLoading] = useState<boolean>(false);

  const [addUsersInChannelOpen, setAddUsersInChannelOpen] = useState<boolean>(false);

  const [updateUsersRoleOpen, setUpdateUsersRoleOpen] = useState<boolean>(false);

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);

  const [userChannels, setUserChannels] = useState<UserChannels | null>(null);

  const [search, setSearch] = useState<string>('');

  const debouncedSearch = useDebounce<string>(search);

  const selectedUserIds = useMemo(() => selectedUsers.map(({ id }) => id), [selectedUsers]);

  const selectedUsersLength = useMemo(() => selectedUsers.length, [selectedUsers.length]);

  const selectedUsersWithBanned = useMemo(
    () => selectedUsers.some(({ isBanned }) => isBanned),
    [selectedUsers]
  );

  const selectedUsersWithUnbanned = useMemo(
    () => selectedUsers.some(({ isBanned }) => !isBanned),
    [selectedUsers]
  );

  const selectedUsersWithUnbannedAndEmail = useMemo(
    () => selectedUsers.some(({ email, isBanned }) => Boolean(email?.trim().length && !isBanned)),
    [selectedUsers]
  );

  const selectedUsersWithInactiveUnbannedAndEmail = useMemo(
    () =>
      selectedUsers.some(({ email, isBanned, authorizedAt }) =>
        Boolean(email?.trim().length && !isBanned && !authorizedAt)
      ),
    [selectedUsers]
  );

  const { isFetched, users, pageInfo } = useAppSelector(
    ({ organisationUsers }) => organisationUsers
  );

  const getUsers = useCallback(
    ({
      page,
      query = search,
      orderBy = orderByActive,
      orderDirection = orderDirectionActive,
      filters = requestFilters,
    }: GetOrgUsersArgs) => {
      dispatch(
        getOrganisationUsers({ page, orderBy, orderDirection, filters: { query, ...filters } })
      );
    },
    [dispatch, orderByActive, orderDirectionActive, requestFilters, search]
  );

  useEffect(
    () => getUsers({ page: 1 }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch, orderByActive, orderDirectionActive]
  );

  useEffect(
    () => {
      // setSelectAll(false);
      setSelectedUsers([]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch]
  );

  // const stats = useMemo(() => {
  //   return (
  //     <div className={classes['users__stats']}>
  //       <div className={classes['users__stats-item']}>
  //         <IconLabel
  //           iconId={'user'}
  //           iconSize={24}
  //           className={classes['users__stats-item-icon']}
  //           singleColor
  //           nonClickable
  //         />
  //         <div className={classes['users__stats-item-text']}>
  //           <div className={classes['users__stats-item-text-label']}>{ctxT('total-users')}</div>
  //           <div className={classes['users__stats-item-text-value']}>
  //             {Math.ceil(Math.random() * 1000)}
  //           </div>
  //         </div>
  //         <Delta
  //           className={classes['users__stats-item-delta']}
  //           value={Math.random() * 50}
  //           valuePrev={Math.random() * 50}
  //         />
  //       </div>
  //       <div className={classes['users__stats-item']}>
  //         <IconLabel
  //           iconId={'user-check'}
  //           iconSize={24}
  //           className={classes['users__stats-item-icon']}
  //           singleColor
  //           nonClickable
  //         />
  //         <div className={classes['users__stats-item-text']}>
  //           <div className={classes['users__stats-item-text-label']}>{ctxT('active-users')}</div>
  //           <div className={classes['users__stats-item-text-value']}>
  //             {Math.ceil(Math.random() * 1000)}
  //           </div>
  //         </div>
  //         <Delta
  //           className={classes['users__stats-item-delta']}
  //           value={Math.random() * 50}
  //           valuePrev={Math.random() * 50}
  //         />
  //       </div>
  //       <div className={classes['users__stats-item']}>
  //         <IconLabel
  //           iconId={'user-cancel'}
  //           iconSize={24}
  //           className={classes['users__stats-item-icon']}
  //           singleColor
  //           nonClickable
  //         />
  //         <div className={classes['users__stats-item-text']}>
  //           <div className={classes['users__stats-item-text-label']}>{ctxT('inactive-users')}</div>
  //           <div className={classes['users__stats-item-text-value']}>
  //             {Math.ceil(Math.random() * 1000)}
  //           </div>
  //         </div>
  //         <Delta
  //           className={classes['users__stats-item-delta']}
  //           value={Math.random() * 50}
  //           valuePrev={Math.random() * 50}
  //         />
  //       </div>
  //     </div>
  //   );
  // }, [ctxT]);

  const bulkActionsComponent = useMemo(() => {
    return (
      <>
        <IconLabel
          iconId={'add-plus'}
          iconSize={18}
          label={ctxT('add-to-channel')}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
          onClick={() => setAddUsersInChannelOpen(true)}
        />

        <IconLabel
          iconId={'edit'}
          iconSize={18}
          label={ctxT('update-role')}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
          onClick={() => setUpdateUsersRoleOpen(true)}
        />

        {selectedUsersWithInactiveUnbannedAndEmail && (
          <>
            <IconLabel
              iconId={'eye-crossed'}
              iconSize={18}
              label={ctxT('create-new-password')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={() => setRegeneratePasswordUsersOpen(true)}
            />
            <IconLabel
              iconId={'bell'}
              iconSize={18}
              label={ctxT('send-reminder')}
              color={getCssVar('--base-link-text-color')}
              hoverColor={getCssVar('--base-link-text-hover-color')}
              onClick={() => setRemindUsersOpen(true)}
            />
          </>
        )}

        {selectedUsersWithBanned && (
          <IconLabel
            iconId={'undo'}
            iconSize={18}
            label={t('common.unban')}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={() => setUnbanUsersOpen(true)}
          />
        )}

        {selectedUsersWithUnbanned && (
          <IconLabel
            iconId={'alert'}
            iconSize={18}
            label={t('common.ban')}
            color={getCssVar('--color-danger')}
            hoverColor={getCssVar('--color-danger-hover')}
            onClick={() => setBanUsersOpen(true)}
          />
        )}

        <IconLabel
          iconId={'delete'}
          iconSize={18}
          label={t('common.delete')}
          color={getCssVar('--color-danger')}
          hoverColor={getCssVar('--color-danger-hover')}
          onClick={() => setRemoveUsersOpen(true)}
        />
      </>
    );
  }, [
    ctxT,
    selectedUsersWithBanned,
    selectedUsersWithInactiveUnbannedAndEmail,
    selectedUsersWithUnbanned,
    t,
  ]);

  const refresh = useCallback(() => {
    getUsers({ page: pageInfo.page });
    setSelectedUsers([]);
  }, [getUsers, pageInfo.page]);

  const banUsersHandler = useCallback(async () => {
    setBanLoading(true);

    try {
      const { userManagementResult } = await dispatch(
        organisationUserBanByUserId({ userIds: selectedUserIds })
      ).unwrap();

      if (userManagementResult.length) {
        const hasSkip = userManagementResult.find(
          ({ status }) => status === OrganisationUserManagementStatus.SKIP
        );

        hasSkip ? toast.info(ctxT('ban-skip')) : toast.success(ctxT('ban-success'));

        setSelectedUsers([]);
        return;
      }

      toast.error(t('common.error-message'));
    } catch (_) {
      toast.error(t('common.error-message'));
    } finally {
      setBanLoading(false);
      setBanUsersOpen(false);
    }
  }, [ctxT, dispatch, selectedUserIds, t]);

  const unbanUsersHandler = useCallback(async () => {
    setUnbanLoading(true);

    try {
      const { userManagementResult } = await dispatch(
        organisationUserUnbanByUserId({ userIds: selectedUserIds })
      ).unwrap();

      if (userManagementResult.length) {
        const hasSkip = userManagementResult.find(
          ({ status }) => status === OrganisationUserManagementStatus.SKIP
        );

        hasSkip ? toast.info(ctxT('unban-skip')) : toast.success(ctxT('unban-success'));

        setSelectedUsers([]);
        return;
      }

      toast.error(t('common.error-message'));
    } catch (_) {
      toast.error(t('common.error-message'));
    } finally {
      setUnbanLoading(false);
      setUnbanUsersOpen(false);
    }
  }, [ctxT, dispatch, selectedUserIds, t]);

  const removeUsersHandler = useCallback(async () => {
    setRemoveLoading(true);

    try {
      const userManagementResult = await organisationMemberRemove({
        userIds: selectedUserIds,
      }).unwrap();

      if (userManagementResult.length) {
        const hasSkip = userManagementResult.find(
          ({ status }) => status === OrganisationUserManagementStatus.SKIP
        );

        hasSkip ? toast.info(ctxT('remove-skip')) : toast.success(ctxT('remove-success'));

        refresh();
        return;
      }

      toast.error(t('common.error-message'));
    } catch (_) {
      toast.error(t('common.error-message'));
    } finally {
      setRemoveLoading(false);
      setRemoveUsersOpen(false);
    }
  }, [ctxT, organisationMemberRemove, refresh, selectedUserIds, t]);

  const regeneratePasswordHandler = useCallback(async () => {
    setRegeneratePasswordLoading(true);

    try {
      const userManagementResult = await organisationUserRegeneratePassword({
        userIds: selectedUserIds,
      }).unwrap();

      if (userManagementResult.length) {
        const hasSkipOrAccess = userManagementResult.find(({ status }) =>
          [OrganisationUserManagementStatus.SKIP, OrganisationUserManagementStatus.ACCESS].includes(
            status
          )
        );

        hasSkipOrAccess
          ? toast.info(ctxT('regenerate-password-skip'))
          : toast.success(ctxT('regenerate-password-success'));

        setSelectedUsers([]);
        return;
      }

      toast.error(t('common.error-message'));
    } catch (_) {
      toast.error(t('common.error-message'));
    } finally {
      setRegeneratePasswordLoading(false);
      setRegeneratePasswordUsersOpen(false);
    }
  }, [ctxT, organisationUserRegeneratePassword, selectedUserIds, t]);

  const remindUsersHandler = useCallback(async () => {
    setRemindUsersLoading(true);

    try {
      const userManagementResult = await organisationUserRemindInvitation({
        userIds: selectedUserIds,
      }).unwrap();

      if (userManagementResult.length) {
        const hasSkipOrAccess = userManagementResult.find(({ status }) =>
          [OrganisationUserManagementStatus.SKIP, OrganisationUserManagementStatus.ACCESS].includes(
            status
          )
        );

        hasSkipOrAccess
          ? toast.info(ctxT('remind-users-skip'))
          : toast.success(ctxT('remind-users-success'));

        setSelectedUsers([]);
        return;
      }

      toast.error(t('common.error-message'));
    } catch (_) {
      toast.error(t('common.error-message'));
    } finally {
      setRemindUsersLoading(false);
      setRemindUsersOpen(false);
    }
  }, [ctxT, organisationUserRemindInvitation, selectedUserIds, t]);

  const exportDataHandler = useCallback(async () => {
    const sent = await organisationUserGenerateReport().unwrap();
    toast(t(`common.${sent ? 'report-sent' : 'error-message'}`));
  }, [organisationUserGenerateReport, t]);

  return (
    <div className={classes['users']}>
      <h1 className={classes['users__title']}>{t('orgLayoutMenu.users')}</h1>
      <div className={classes['users__intro']}>{ctxT('intro')}</div>

      {/* {stats} */}

      <div
        className={classNames(classes['users__header'], {
          [classes['users__header--narrow']]: isNarrowViewport,
        })}
      >
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={t('common.type-to-search')}
          className={classes['users__header-search']}
        />
        <div className={classes['users__header-buttons']}>
          <UsersColumns />

          <IconLabel
            iconId={'filter'}
            iconSize={18}
            label={`${t('common.filter')} (${appliedFiltersCount})`}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={() => setFiltersOpen(true)}
          />

          <IconLabel
            iconId={'donwload'}
            iconSize={18}
            data={ctxT('export')}
            hintDirection={IconLabelHintDirection.top}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={exportDataHandler}
          />

          <Button
            type={ButtonType.primary}
            label={ctxT('add-users')}
            onClick={() => setAddUsersOpen(true)}
          />
        </div>
      </div>

      <UsersTable
        isFetched={isFetched}
        users={users}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        orderByActive={orderByActive}
        setOrderByActive={setOrderByActive}
        orderDirectionActive={orderDirectionActive}
        setOrderDirectionActive={setOrderDirectionActive}
        getUsers={getUsers}
        pageInfo={pageInfo}
        setUserChannels={setUserChannels}
        bulkActionsComponent={bulkActionsComponent}
      />

      <ConfirmationModal
        isOpen={banUsersOpen}
        onClose={() => setBanUsersOpen(false)}
        acceptLabel={t('common.ban')}
        onAccept={banUsersHandler}
        title={ctxT('ban-users', { count: selectedUsersLength })}
        subTitle={ctxT('ban-users-sub-title')}
        loading={banLoading}
        danger
      />

      <ConfirmationModal
        isOpen={unbanUsersOpen}
        onClose={() => setUnbanUsersOpen(false)}
        acceptLabel={t('common.unban')}
        onAccept={unbanUsersHandler}
        title={ctxT('unban-users', { count: selectedUsersLength })}
        subTitle={ctxT('unban-users-sub-title')}
        loading={unbanLoading}
      />

      <ConfirmationModal
        isOpen={removeUsersOpen}
        onClose={() => setRemoveUsersOpen(false)}
        acceptLabel={t('common.delete')}
        onAccept={removeUsersHandler}
        title={ctxT('remove-users', { count: selectedUsersLength })}
        subTitle={ctxT('remove-users-sub-title')}
        loading={removeLoading}
        danger
      />

      <ConfirmationModal
        isOpen={regeneratePasswordUsersOpen}
        onClose={() => setRegeneratePasswordUsersOpen(false)}
        acceptLabel={t('common.accept')}
        onAccept={regeneratePasswordHandler}
        title={ctxT('regenerate-password', { count: selectedUsersLength })}
        subTitle={ctxT('regenerate-password-sub-title')}
        loading={regeneratePasswordLoading}
      />

      <ConfirmationModal
        isOpen={remindUsersOpen}
        onClose={() => setRemindUsersOpen(false)}
        acceptLabel={t('common.accept')}
        onAccept={remindUsersHandler}
        title={ctxT('remind-users', { count: selectedUsersLength })}
        subTitle={ctxT('remind-users-sub-title')}
        loading={remindUsersLoading}
      />

      <UserChannelsModal
        userChannels={userChannels}
        setUserChannels={setUserChannels}
        callback={refresh}
      />

      <AddUsersModal isOpen={addUsersOpen} setIsOpen={setAddUsersOpen} callback={refresh} />

      <AddUsersInChannelModal
        isOpen={addUsersInChannelOpen}
        setIsOpen={setAddUsersInChannelOpen}
        userIds={selectedUserIds}
        showNotifyUsers={selectedUsersWithUnbannedAndEmail}
        callback={refresh}
      />

      <UpdateUsersRoleModal
        isOpen={updateUsersRoleOpen}
        setIsOpen={setUpdateUsersRoleOpen}
        userIds={selectedUserIds}
        callback={refresh}
      />

      <ModalSlidePanel
        isOpen={filtersOpen}
        body={<UsersFilters getUsers={getUsers} totalItems={pageInfo.totalItems} />}
        onClose={() => setFiltersOpen(false)}
        ariaLabel={t('common.filter')}
      />
    </div>
  );
};
